import { useState, useEffect } from 'react';
import { Card, Text, Group, Stack, NumberInput, Select } from '@mantine/core';
import { IconUsers, IconClock } from '@tabler/icons-react';
import { BusyBadge } from './BusyBadge';
import { fetchAvailability, type ZoneInfo, type AvailabilityResult } from '../api-client';

function formatPrice(p: ZoneInfo['pricing']): string {
  if (!p) return '';
  const unit = p.unit === 'per_hour_per_person' ? '/人/時' : '/格/時';
  return `${p.currency} ${p.amount}${unit}`;
}

const TIME_SLOTS = [
  { value: '10', label: '10:00 – 12:00' },
  { value: '12', label: '12:00 – 14:00' },
  { value: '14', label: '14:00 – 16:00' },
  { value: '16', label: '16:00 – 18:00' },
  { value: '18', label: '18:00 – 20:00' },
  { value: '20', label: '20:00 – 22:00' },
];

/**
 * Build ISO UTC timestamps for "today at HH:00 local time" → "HH+2:00 local time".
 * Uses the browser's local timezone so the slot label matches what users see.
 * If the slot is already in the past (today's 10:00 when it's already 15:00), bumps
 * to tomorrow so the reservation "start must be in future" check passes.
 */
function todaySlotIso(hour: number): { start: string; end: string } {
  const start = new Date();
  start.setHours(hour, 0, 0, 0);
  if (start.getTime() <= Date.now()) {
    start.setDate(start.getDate() + 1);
  }
  const end = new Date(start);
  end.setHours(end.getHours() + 2);
  return { start: start.toISOString(), end: end.toISOString() };
}

export function ZoneAvailability({
  zone,
  venueId,
}: {
  zone: ZoneInfo;
  venueId: string;
}) {
  const [partySize, setPartySize] = useState<number>(zone.minPartySize);
  const [slot, setSlot] = useState<string>('14');
  const [result, setResult] = useState<AvailabilityResult | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const { start, end } = todaySlotIso(Number(slot));
    setLoading(true);
    fetchAvailability({
      venueId,
      serviceId: zone.serviceId,
      scenarioType: zone.scenarioType,
      resourceId: zone.resourceId,
      partySize,
      startTime: start,
      endTime: end,
    })
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch(() => {
        if (!cancelled) setResult(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [venueId, zone.serviceId, zone.scenarioType, zone.resourceId, partySize, slot]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={700} size="xl">
            {zone.zoneLabel}
          </Text>
          {result && !loading && <BusyBadge level={result.busyLevel} />}
        </Group>

        <Group gap="xs">
          <IconUsers size={16} />
          <Text size="sm" c="dimmed">
            {zone.minPartySize}–{zone.maxPartySize} 人
          </Text>
          {zone.pricing && (
            <Text size="sm" c="dimmed" ml="xs">
              {formatPrice(zone.pricing)}
            </Text>
          )}
        </Group>

        <Group grow>
          <NumberInput
            label="人數"
            value={partySize}
            onChange={(v) => setPartySize(typeof v === 'number' ? v : zone.minPartySize)}
            min={zone.minPartySize}
            max={zone.maxPartySize}
            size="sm"
          />
          <Select
            label="時段"
            data={TIME_SLOTS}
            value={slot}
            onChange={(v) => setSlot(v ?? '14')}
            leftSection={<IconClock size={16} />}
            size="sm"
          />
        </Group>

        {loading && (
          <Text size="sm" c="dimmed">
            查詢中...
          </Text>
        )}
        {result && !loading && (
          <Text size="sm">
            {result.hasAvailability
              ? '可預約 — 請來電或現場洽詢'
              : '此時段已額滿，請選擇其他時段'}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
